import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type {
  SparkActivityDetail,
  SparkActivityStatus,
  UpdateSparkActivityPayload,
  SparkActivityStageForm,
} from '../../types/spark-activity';
import { useUpdateSparkActivity } from '../../hooks/useSparkActivities';
import { useUpdateSparkActivityStatus } from '../../hooks/useSparkActivities';
import ActivityActionBar from './ActivityActionBar';
import ArchiveConfirmationModal from './ArchiveConfirmationModal';
import UnsavedChangesModal from './UnsavedChangesModal';
import ClassificationPanel from './ClassificationPanel';
import StageLadder from './StageLadder';
import MismatchResolutionPanel from './MismatchResolutionPanel';
import { validateActivityPayload } from './validateActivityPayload';
import { buildValidationToast } from './buildValidationToast';
import PineSelect from './PineSelect';

const STATUS_LABELS: Record<SparkActivityStatus, string> = {
  draft: 'Draft',
  published: 'Live',
  unpublished: 'Unpublished',
  archived: 'Archived',
};

const STATUS_BADGE_COLORS: Record<SparkActivityStatus, string> = {
  draft: 'border-[1.5px] border-pine-draft text-pine-draft bg-white',
  published: 'border-[1.5px] border-pine-approved text-pine-approved bg-white',
  unpublished: 'border-[1.5px] border-pine-draft text-pine-draft bg-white',
  archived: 'border-[1.5px] border-pine-danger text-pine-danger bg-white',
};

const DURATION_OPTIONS = [
  { label: '5–10 min', value: '5-10', duration: 5, durationMax: 10 },
  { label: '10–15 min', value: '10-15', duration: 10, durationMax: 15 },
  { label: '15–20 min', value: '15-20', duration: 15, durationMax: 20 },
  { label: '20–30 min', value: '20-30', duration: 20, durationMax: 30 },
];

function getDurationValue(duration: number, durationMax: number | null): string {
  const match = DURATION_OPTIONS.find(
    (o) => o.duration === duration && o.durationMax === durationMax
  );
  return match?.value ?? `${duration}-${durationMax ?? duration}`;
}

interface ActivityEditPanelProps {
  activity: SparkActivityDetail;
  currentIndex: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onArchived?: () => void;
  formData: UpdateSparkActivityPayload | null;
  setFormData: React.Dispatch<React.SetStateAction<UpdateSparkActivityPayload | null>>;
  focusedStage: number | null;
  onFocusedStageChange: (index: number | null) => void;
  onTogglePreview: () => void;
  previewVisible: boolean;
}

export default function ActivityEditPanel({
  activity,
  currentIndex,
  total,
  onPrev,
  onNext,
  onDirtyChange,
  onArchived,
  formData,
  setFormData,
  focusedStage,
  onFocusedStageChange,
  onTogglePreview,
  previewVisible,
}: ActivityEditPanelProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [imageValue, setImageValue] = useState<{ documentId: number; viewUrl: string } | null>(null);

  const updateMutation = useUpdateSparkActivity();
  const statusMutation = useUpdateSparkActivityStatus();

  useEffect(() => {
    const sortedStages = [...activity.stages].sort((a, b) => a.level - b.level);
    const stages: SparkActivityStageForm[] = sortedStages.map((s) => ({
      communicationStageId: s.communicationStageId,
      description: s.description ?? '',
      points: Array.isArray(s.points) ? s.points.map((p) => ({ ...p, _id: crypto.randomUUID() })) : [],
      whyItWorks: s.whyItWorks ?? '',
      expertTips: s.expertTips ?? '',
      domainId: activity.domains.find((d) => d.level === s.level)?.domainId ?? 0,
      skillIds: activity.skills.filter((sk) => sk.level === s.level).map((sk) => sk.skillId),
      active: s.active,
    }));

    setFormData({
      title: activity.title,
      prep: activity.prep ?? '',
      duration: activity.duration,
      durationMax: activity.durationMax,
      stages,
      playIds: activity.plays.map((p) => p.playId),
      preferenceIds: activity.preferences.map((p) => p.preferenceId),
      packIds: activity.packs.map((p) => p.packId),
      imageDocumentId: activity.imageDocumentId,
    });
    setImageValue(
      activity.imageUrl && activity.imageDocumentId
        ? { documentId: activity.imageDocumentId, viewUrl: activity.imageUrl }
        : null
    );
    setIsDirty(false);
    setErrors({});
  }, [activity.id]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleFieldChange = (field: keyof UpdateSparkActivityPayload, value: unknown) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
    setIsDirty(true);
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined as unknown as string }));
  };

  const handleSave = async () => {
    if (!formData) return;
    const validationErrors = validateActivityPayload(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error(buildValidationToast(validationErrors));
      return;
    }
    const savePayload: UpdateSparkActivityPayload = {
      ...formData,
      stages: formData.stages.map((stage) => ({
        ...stage,
        points: stage.points.map(({ _id: _unused, ...p }) => p),
      })),
    };
    await updateMutation.mutateAsync({ id: activity.id, data: savePayload });
    setIsDirty(false);
  };

  const handleStatusTransition = () => {
    const nextStatus =
      activity.status === 'draft' ? 'published'
      : activity.status === 'published' ? 'unpublished'
      : activity.status === 'unpublished' ? 'published'
      : null;
    if (!nextStatus) return;
    if (nextStatus === 'published' && formData) {
      const validationErrors = validateActivityPayload(formData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        toast.error(buildValidationToast(validationErrors));
        return;
      }
    }
    statusMutation.mutate({ id: activity.id, status: nextStatus });
  };

  const handleArchiveConfirm = () => {
    statusMutation.mutate(
      { id: activity.id, status: 'archived' },
      {
        onSuccess: () => {
          setShowArchiveModal(false);
          onArchived?.();
        },
      }
    );
  };

  const handleStageChange = (index: number, field: keyof SparkActivityStageForm, value: unknown) => {
    setFormData((prev) => {
      if (!prev) return null;
      const newStages = [...prev.stages];
      newStages[index] = { ...newStages[index], [field]: value };
      return { ...prev, stages: newStages };
    });
    setIsDirty(true);
    if (field === 'active' && value === false) {
      setErrors((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          if (key.startsWith(`stages.${index}.`)) delete next[key];
        }
        return next;
      });
    } else {
      const errorKey = `stages.${index}.${field}`;
      if (errors[errorKey]) setErrors((prev) => ({ ...prev, [errorKey]: undefined as unknown as string }));
    }
  };

  const guardedNav = (cb: () => void) => {
    if (isDirty) {
      setPendingNav(() => cb);
    } else {
      cb();
    }
  };

  const handleImageChange = (image: { documentId: number; viewUrl: string } | null) => {
    setImageValue(image);
    handleFieldChange('imageDocumentId', image?.documentId ?? null);
  };

  const handlePrev = () => guardedNav(onPrev);
  const handleNext = () => guardedNav(onNext);

  if (!formData) return null;

  const durationValue = getDurationValue(formData.duration, formData.durationMax);

  return (
    <>
      <div data-testid="activity-edit-panel" className="admin-card p-[30px] relative">
        {/* Activity Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-pine-label uppercase tracking-[0.12em] font-bold text-pine-ink-soft text-[11px]">
              Activity {currentIndex + 1} of {total}
            </p>
            {activity.batchName && (
              <span className="inline-block mt-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-pine-hover-item text-pine-ink-soft border border-pine-line">
                {activity.batchName}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGE_COLORS[activity.status]}`}
            >
              {STATUS_LABELS[activity.status]}
            </span>
            <button
              onClick={onTogglePreview}
              className="p-1.5 rounded-md hover:bg-pine-hover-item transition-colors text-pine-ink-soft hover:text-pine-ink"
              title={previewVisible ? 'Hide preview' : 'Show preview'}
            >
              {/* Phone icon */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                <line x1="12" y1="18" x2="12.01" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Title */}
        <input
          data-testid="activity-title-input"
          type="text"
          value={formData.title}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          placeholder="Activity title"
          className={`w-full font-display text-[31px] font-semibold bg-transparent border-b-2 outline-none transition-colors
            ${errors.title ? 'border-pine-danger' : 'border-transparent hover:border-pine-line focus:border-pine-pine'}`}
        />
        {errors.title && (
          <p className="text-[11px] text-pine-danger mt-1">{errors.title}</p>
        )}

        {/* Prep + Duration */}
        <div className="grid grid-cols-2 gap-[26px] mt-[26px]">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mb-1">
              What you need / Prep
            </label>
            <input
              data-testid="prep-input"
              type="text"
              value={formData.prep}
              onChange={(e) => handleFieldChange('prep', e.target.value)}
              placeholder="e.g. No materials needed"
              className="w-full border-[1.5px] border-pine-line rounded-pine-input px-3 py-2 text-[13px] hover:border-pine-pine focus:border-pine-pine focus:outline-none bg-white transition-colors"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mb-1">
              Duration
            </label>
            <div data-testid="duration-select">
              <PineSelect
                options={DURATION_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                value={durationValue}
                onChange={(v) => {
                  const opt = DURATION_OPTIONS.find((o) => o.value === v);
                  if (opt) {
                    handleFieldChange('duration', opt.duration);
                    handleFieldChange('durationMax', opt.durationMax);
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Mismatch Resolution */}
        {activity.mismatch && (
          <MismatchResolutionPanel
            activityId={activity.id}
            mismatch={activity.mismatch}
          />
        )}

        {/* Classification Panel */}
        <ClassificationPanel
          formData={formData}
          onChange={handleFieldChange}
          imageValue={imageValue}
          onImageChange={handleImageChange}
        />

        {/* Stage Ladder */}
        <StageLadder
          stages={formData.stages}
          onStageChange={handleStageChange}
          errors={errors}
          focusedCol={focusedStage}
          onFocusedColChange={onFocusedStageChange}
        />

        {/* Action Bar */}
        <ActivityActionBar
          onSave={handleSave}
          onStatusTransition={handleStatusTransition}
          onArchive={() => setShowArchiveModal(true)}
          onPrev={handlePrev}
          onNext={handleNext}
          isSaving={updateMutation.isPending}
          isArchiving={statusMutation.isPending}
          isDirty={isDirty}
          status={activity.status}
          currentIndex={currentIndex}
          total={total}
        />
      </div>

      <UnsavedChangesModal
        open={pendingNav !== null}
        onLeave={() => {
          const nav = pendingNav;
          setPendingNav(null);
          setIsDirty(false);
          nav?.();
        }}
        onStay={() => setPendingNav(null)}
      />

      <ArchiveConfirmationModal
        open={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchiveConfirm}
        isLoading={statusMutation.isPending}
      />
    </>
  );
}
